import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import ReactFlow, {
  Background, Controls, MiniMap,
  useNodesState, useEdgesState, addEdge,
  Handle, Position,
  type Node, type Edge, type Connection, type NodeProps,
} from 'reactflow'
// @ts-ignore
import 'reactflow/dist/style.css'
import { getFirestore, doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore'

const API_BASE = (import.meta as any).env.VITE_API_URL as string
const WORKER_BASE = API_BASE.replace(/\/api\/?$/, '')

// ───────────────────────── Componentes dos nodes ─────────────────────────

function TriggerNode() {
  return (
    <div className="rounded-xl border-2 border-emerald-500 bg-emerald-500/10 px-4 py-3 min-w-[180px]">
      <p className="text-emerald-400 text-xs font-semibold mb-0.5">🟢 GATILHO</p>
      <p className="text-white text-sm font-medium">Lead Capturado</p>
      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}

const MEDIA_LABELS: Record<string, { icon: string; label: string }> = {
  none:  { icon: '💬', label: 'MENSAGEM WHATSAPP' },
  image: { icon: '🖼️', label: 'ENVIAR IMAGEM' },
  video: { icon: '🎥', label: 'ENVIAR VÍDEO' },
  audio: { icon: '🎤', label: 'ENVIAR ÁUDIO' },
}

function WhatsAppNode({ data }: NodeProps) {
  const mediaType = data.mediaType || 'none'
  const { icon, label } = MEDIA_LABELS[mediaType] || MEDIA_LABELS.none

  return (
    <div className="rounded-xl border-2 border-indigo-500 bg-indigo-500/10 px-4 py-3 min-w-[200px] max-w-[240px]">
      <Handle type="target" position={Position.Top} />
      <p className="text-indigo-400 text-xs font-semibold mb-0.5">{icon} {label}</p>
      {mediaType !== 'none' ? (
        <p className="text-white text-sm truncate">
          {data.mediaUrl ? '✅ Arquivo configurado' : <span className="text-gray-500 italic">Clique para configurar</span>}
        </p>
      ) : (
        <p className="text-white text-sm truncate">
          {data.message ? data.message : <span className="text-gray-500 italic">Clique para configurar</span>}
        </p>
      )}
      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}

function WaitNode({ data }: NodeProps) {
  return (
    <div className="rounded-xl border-2 border-yellow-500 bg-yellow-500/10 px-4 py-3 min-w-[180px]">
      <Handle type="target" position={Position.Top} />
      <p className="text-yellow-400 text-xs font-semibold mb-0.5">⏱️ ESPERAR</p>
      <p className="text-white text-sm">{data.amount} {data.unit === 'minutes' ? 'minuto(s)' : data.unit === 'hours' ? 'hora(s)' : 'dia(s)'}</p>
      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}

function ConditionNode() {
  return (
    <div className="rounded-xl border-2 border-purple-500 bg-purple-500/10 px-4 py-3 min-w-[200px] relative pb-6">
      <Handle type="target" position={Position.Top} />
      <p className="text-purple-400 text-xs font-semibold mb-0.5">❓ CONDIÇÃO</p>
      <p className="text-white text-sm">Respondeu no WhatsApp?</p>
      <Handle type="source" position={Position.Bottom} id="yes" style={{ left: '25%', background: '#10b981' }} />
      <Handle type="source" position={Position.Bottom} id="no" style={{ left: '75%', background: '#ef4444' }} />
      <div className="flex justify-between text-[10px] mt-1 px-1">
        <span className="text-emerald-400">Sim</span>
        <span className="text-red-400">Não</span>
      </div>
    </div>
  )
}

function CrmNode({ data }: NodeProps) {
  return (
    <div className="rounded-xl border-2 border-sky-500 bg-sky-500/10 px-4 py-3 min-w-[200px]">
      <Handle type="target" position={Position.Top} />
      <p className="text-sky-400 text-xs font-semibold mb-0.5">📋 MOVER NO CRM</p>
      <p className="text-white text-sm">
        {data.columnName ? data.columnName : <span className="text-gray-500 italic">Selecione uma coluna</span>}
      </p>
      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}

const nodeTypes = {
  trigger: TriggerNode,
  whatsapp: WhatsAppNode,
  wait: WaitNode,
  condition: ConditionNode,
  crm: CrmNode,
}

// ───────────────────────── Upload de mídia ─────────────────────────

function MediaUploadField({
  mediaType,
  mediaUrl,
  onUploaded,
}: {
  mediaType: string
  mediaUrl: string
  onUploaded: (url: string) => void
}) {
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  async function uploadFile(file: File) {
    setUploading(true)
    setUploadError('')
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${API_BASE}/media/upload`, { method: 'POST', body: formData })
      const data = await res.json()
      if (!data.success) throw new Error(data.error || 'Erro ao enviar arquivo')
      const fullUrl = data.url.startsWith('http') ? data.url : `${WORKER_BASE}${data.url}`
   onUploaded(fullUrl)
    } catch (err: any) {
      setUploadError(err.message)
    } finally {
      setUploading(false)
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) uploadFile(file)
  }

  const accept = mediaType === 'image' ? 'image/*' : mediaType === 'video' ? 'video/*' : 'audio/*'

  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-indigo-500 bg-indigo-500/10' : 'border-white/20 hover:border-white/40'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f) }}
        />
        {uploading ? (
          <p className="text-xs text-gray-400">Enviando...</p>
        ) : (
          <p className="text-xs text-gray-400">Arraste um arquivo aqui ou clique pra escolher do dispositivo</p>
        )}
      </div>

      {uploadError && <p className="text-xs text-red-400 mt-1">{uploadError}</p>}

      <p className="text-xs text-gray-500 mt-2 mb-1">Ou cole uma URL direto:</p>
      <input
        type="text"
        value={mediaUrl}
        onChange={e => onUploaded(e.target.value)}
        className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
        placeholder="https://..."
      />

      {mediaUrl && (
        <div className="mt-2">
          {mediaType === 'image' && <img src={mediaUrl} className="max-h-32 rounded-lg" />}
          {mediaType === 'video' && <video src={mediaUrl} controls className="max-h-32 rounded-lg w-full" />}
          {mediaType === 'audio' && <audio src={mediaUrl} controls className="w-full" />}
        </div>
      )}
    </div>
  )
}

// ───────────────────────────── Página principal ─────────────────────────────

export default function FunnelEditorPage() {
  const { campaignId } = useParams<{ campaignId: string }>()
  const navigate = useNavigate()

  const [nodes, setNodes, onNodesChange] = useNodesState<any>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [campaignName, setCampaignName] = useState('')
  const [crmColumns, setCrmColumns] = useState<{ id: string; name: string }[]>([])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      if (!campaignId) return
      try {
        const db = getFirestore()

        const campaignSnap = await getDoc(doc(db, 'campaigns', campaignId))
        const campaignData = campaignSnap.data() as any
        setCampaignName(campaignData?.briefing?.productName || 'Campanha')

        const existingFunnel = campaignData?.funnel
        if (existingFunnel?.nodes?.length) {
          setNodes(existingFunnel.nodes)
          setEdges(existingFunnel.edges || [])
        } else {
          setNodes([
            { id: 'trigger', type: 'trigger', position: { x: 250, y: 50 }, data: {}, deletable: false },
          ])
        }

        const columnsSnap = await getDocs(collection(db, 'crm_columns'))
        const columns: { id: string; name: string }[] = []
        columnsSnap.forEach(c => {
          const cd = c.data() as any
          columns.push({ id: c.id, name: cd.name || cd.title || cd.label || c.id })
        })
        setCrmColumns(columns)
      } catch (err: any) {
        setError('Erro ao carregar funil: ' + err.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [campaignId])

  const onConnect = useCallback(
    (connection: Connection) => setEdges((eds: any) => addEdge(connection, eds)),
    [setEdges]
  )

  function addNode(type: 'whatsapp' | 'wait' | 'condition' | 'crm') {
    const defaultData: Record<string, any> = {
      whatsapp: { message: '', mediaType: 'none', mediaUrl: '', simulateRecording: false },
      wait: { amount: 1, unit: 'hours' },
      condition: {},
      crm: { columnName: '', columnId: '' },
    }
    const id = `${type}-${Date.now()}`
    const offset = nodes.length
    const newNode: Node = {
      id,
      type,
      position: { x: 100 + (offset % 4) * 220, y: 200 + Math.floor(offset / 4) * 160 },
      data: defaultData[type],
    }
    setNodes((nds: any) => [...nds, newNode])
  }

  function updateSelectedNodeData(changes: Record<string, any>) {
    setNodes((nds: any) => nds.map((n: any) =>
      n.id === selectedNodeId ? { ...n, data: { ...n.data, ...changes } } : n
    ))
  }

  function removeSelectedNode() {
    if (!selectedNodeId) return
    setNodes((nds: any) => nds.filter((n: any) => n.id !== selectedNodeId))
setEdges((eds: any) => eds.filter((e: any) => e.source !== selectedNodeId && e.target !== selectedNodeId))
    setSelectedNodeId(null)
  }

  async function handleSave() {
    if (!campaignId) return
    setSaving(true)
    setError('')
    try {
      const db = getFirestore()
      await setDoc(doc(db, 'campaigns', campaignId), {
        funnel: { nodes, edges },
        funnelJson: JSON.stringify({ nodes, edges }),
      }, { merge: true })
    } catch (err: any) {
      setError('Erro ao salvar funil: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const selectedNode = nodes.find(n => n.id === selectedNodeId)
  const mediaType = selectedNode?.data?.mediaType || 'none'

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">Carregando funil...</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/funis')} className="text-gray-400 hover:text-white transition-colors">
            ← Voltar
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">{campaignName}</h1>
            <p className="text-xs text-gray-500">Funil de automação</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg,#6366f1,#10b981)' }}
        >
          {saving ? 'Salvando...' : 'Salvar Funil'}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3 mb-4 text-sm flex-shrink-0">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 flex-shrink-0 flex-wrap">
        <button onClick={() => addNode('whatsapp')} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/30 transition-colors">
          + Mensagem WhatsApp
        </button>
        <button onClick={() => addNode('wait')} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/30 transition-colors">
          + Esperar
        </button>
        <button onClick={() => addNode('condition')} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 transition-colors">
          + Condição: Respondeu?
        </button>
        <button onClick={() => addNode('crm')} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-sky-500/20 text-sky-300 border border-sky-500/30 hover:bg-sky-500/30 transition-colors">
          + Mover no CRM
        </button>
        <span className="text-xs text-gray-500 ml-2">Clique num node pra configurar.</span>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        <div className="flex-1 rounded-2xl border border-white/10 overflow-hidden bg-[#0a0a0f]">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            onNodeClick={(_: any, node: any) => setSelectedNodeId(node.id)}
            onPaneClick={() => setSelectedNodeId(null)}
            fitView
          >
            <Background color="#333" gap={20} />
            <Controls />
            <MiniMap style={{ background: '#111' }} maskColor="rgba(0,0,0,0.6)" />
          </ReactFlow>
        </div>

        {selectedNode && selectedNode.type !== 'trigger' && (
          <div className="w-72 flex-shrink-0 bg-white/5 border border-white/10 rounded-2xl p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-sm">Configurar node</h3>
              <button
                onClick={removeSelectedNode}
                className="text-red-400 hover:text-red-300 text-xs font-medium"
              >
                🗑️ Excluir
              </button>
            </div>

            {selectedNode.type === 'whatsapp' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Tipo de conteúdo</label>
                  <select
                    value={mediaType}
                    onChange={e => updateSelectedNodeData({ mediaType: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
                  >
                    <option value="none" className="bg-gray-900">Texto</option>
                    <option value="image" className="bg-gray-900">Imagem</option>
                    <option value="video" className="bg-gray-900">Vídeo</option>
                    <option value="audio" className="bg-gray-900">Áudio</option>
                  </select>
                </div>

                {mediaType !== 'none' && (
                  <MediaUploadField
                    mediaType={mediaType}
                    mediaUrl={selectedNode.data.mediaUrl || ''}
                    onUploaded={(uploadedUrl) => updateSelectedNodeData({ mediaUrl: uploadedUrl })}
                  />
                )}

                {mediaType === 'audio' && (
                  <label className="flex items-start gap-2 text-xs text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selectedNode.data.simulateRecording}
                      onChange={e => updateSelectedNodeData({ simulateRecording: e.target.checked })}
                      className="mt-0.5"
                    />
                    <span>Mostrar "gravando áudio..." pro lead antes de enviar (igual o WhatsApp de verdade)</span>
                  </label>
                )}

                {mediaType === 'audio' ? (
                  <p className="text-xs text-gray-500">Áudios de voz no WhatsApp não têm legenda.</p>
                ) : (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      {mediaType === 'none' ? 'Mensagem' : 'Legenda (opcional)'}
                    </label>
                    <textarea
                      value={selectedNode.data.message || ''}
                      onChange={e => updateSelectedNodeData({ message: e.target.value })}
                      rows={4}
                      className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 resize-none"
                      placeholder="Olá {nome}! ..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Use <code className="text-gray-400">{'{nome}'}</code> pra inserir o nome do lead.</p>
                  </div>
                )}
              </div>
            )}

            {selectedNode.type === 'wait' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Quantidade</label>
                  <input
                    type="number"
                    min={1}
                    value={selectedNode.data.amount || 1}
                    onChange={e => updateSelectedNodeData({ amount: Number(e.target.value) })}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Unidade</label>
                  <select
                    value={selectedNode.data.unit || 'hours'}
                    onChange={e => updateSelectedNodeData({ unit: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
                  >
                    <option value="minutes" className="bg-gray-900">Minutos</option>
                    <option value="hours" className="bg-gray-900">Horas</option>
                    <option value="days" className="bg-gray-900">Dias</option>
                  </select>
                </div>
              </div>
            )}

            {selectedNode.type === 'condition' && (
              <p className="text-xs text-gray-400">
                Esse node verifica se o lead respondeu alguma mensagem no WhatsApp. Conecte a saída <span className="text-emerald-400">Sim</span> a um próximo passo pra quem respondeu, e a saída <span className="text-red-400">Não</span> pra quem não respondeu.
              </p>
            )}

            {selectedNode.type === 'crm' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Coluna do CRM</label>
                <select
                  value={selectedNode.data.columnId || ''}
                  onChange={e => {
                    const col = crmColumns.find(c => c.id === e.target.value)
                    updateSelectedNodeData({ columnId: e.target.value, columnName: col?.name || '' })
                  }}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
                >
                  <option value="" className="bg-gray-900">Selecione...</option>
                  {crmColumns.map(c => (
                    <option key={c.id} value={c.id} className="bg-gray-900">{c.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}